/**
 * Image compression utility for Palm Oracle
 * Ensures images are within upload size limits to prevent 520 errors
 * But not too small to fail backend validation
 */

// Size limits (in bytes)
const MAX_UPLOAD_SIZE = 800 * 1024; // 800KB max to avoid proxy limits
const MIN_UPLOAD_SIZE = 100 * 1024; // 100KB min to pass backend validation
const MAX_DIMENSION = 1500; // Max width/height in pixels (increased from 1200)
const MIN_DIMENSION = 800;  // Min dimension to ensure quality
const INITIAL_QUALITY = 0.92; // Higher initial quality
const MIN_QUALITY = 0.70;     // Don't go below 70% quality

/**
 * Compress an image to fit within size limits
 * @param {string} base64Image - Base64 encoded image (with or without data URL prefix)
 * @param {Object} options - Compression options
 * @returns {Promise<string>} - Compressed base64 image
 */
export const compressImage = async (base64Image, options = {}) => {
  const maxSize = options.maxSize || MAX_UPLOAD_SIZE;
  const minSize = options.minSize || MIN_UPLOAD_SIZE;
  const maxDimension = options.maxDimension || MAX_DIMENSION;
  
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      try {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        const originalWidth = width;
        const originalHeight = height;
        
        // Only resize if larger than max dimension
        if (width > maxDimension || height > maxDimension) {
          const ratio = Math.min(maxDimension / width, maxDimension / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        
        // Ensure minimum dimension for quality
        if (width < MIN_DIMENSION && height < MIN_DIMENSION) {
          // Image is already small, don't resize further
          width = originalWidth;
          height = originalHeight;
        }
        
        // Create canvas for resizing
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        
        // Start with high quality
        let quality = INITIAL_QUALITY;
        let result = canvas.toDataURL('image/jpeg', quality);
        let currentSize = getBase64Size(result);
        
        // Reduce quality only if needed and not below minimum
        while (currentSize > maxSize && quality > MIN_QUALITY) {
          quality -= 0.05; // Smaller steps for better control
          result = canvas.toDataURL('image/jpeg', quality);
          currentSize = getBase64Size(result);
        }
        
        // If compressed too much, use higher quality even if larger
        if (currentSize < minSize && quality < INITIAL_QUALITY) {
          // Try with initial quality
          result = canvas.toDataURL('image/jpeg', INITIAL_QUALITY);
          currentSize = getBase64Size(result);
          quality = INITIAL_QUALITY;
        }
        
        // Log compression info for debugging
        const originalSize = getBase64Size(base64Image);
        const compressedSize = getBase64Size(result);
        const reduction = Math.round((1 - compressedSize / originalSize) * 100);
        console.log(`Image compression: ${(originalSize/1024).toFixed(1)}KB → ${(compressedSize/1024).toFixed(1)}KB (${reduction}% reduction, quality: ${quality.toFixed(2)}, ${width}x${height})`);
        
        // Warn if still too small
        if (compressedSize < minSize) {
          console.warn(`Warning: Compressed image (${(compressedSize/1024).toFixed(1)}KB) may be too small for backend validation`);
        }
        
        resolve(result);
      } catch (err) {
        reject(new Error('Görsel sıkıştırılamadı: ' + err.message));
      }
    };
    
    img.onerror = () => {
      reject(new Error('Görsel yüklenemedi'));
    };
    
    // Ensure proper data URL format
    if (!base64Image.startsWith('data:')) {
      img.src = `data:image/jpeg;base64,${base64Image}`;
    } else {
      img.src = base64Image;
    }
  });
};

/**
 * Get the size of a base64 string in bytes
 * @param {string} base64 - Base64 string
 * @returns {number} - Size in bytes
 */
export const getBase64Size = (base64) => {
  // Remove data URL prefix if present
  const base64String = base64.replace(/^data:image\/\w+;base64,/, '');
  // Calculate actual byte size (base64 encodes 3 bytes in 4 chars)
  const padding = (base64String.match(/=+$/) || [''])[0].length;
  return Math.floor((base64String.length * 3) / 4) - padding;
};

/**
 * Check if image needs compression (only if over max size)
 * @param {string} base64Image - Base64 image
 * @returns {boolean}
 */
export const needsCompression = (base64Image) => {
  return getBase64Size(base64Image) > MAX_UPLOAD_SIZE;
};

/**
 * Format bytes to human readable string
 * @param {number} bytes 
 * @returns {string}
 */
export const formatBytes = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
};

export default {
  compressImage,
  getBase64Size,
  needsCompression,
  formatBytes,
  MAX_UPLOAD_SIZE,
  MIN_UPLOAD_SIZE,
  MAX_DIMENSION
};
